/**
 * Web analytics reporter — the single core sink the whole app emits
 * through. Forwards every product event to both configured backends
 * (Google Analytics 4, Amplitude), which are loaded at runtime by
 * `src/cookie/consent.ts` from page load — in a storage-free mode until
 * the visitor consents, upgraded to full tracking on consent.
 *
 * Design (safe-client rules #1 + #2):
 *  - **Fail-safe.** Each forward is wrapped in try/catch and no-ops when
 *    its global is absent — a missing `gtag` / `amplitude` is the
 *    NORMAL pre-consent / dev / blocked state, not an error. An analytics
 *    emit must NEVER throw into the host (especially the cold-start map).
 *  - **Production-only emit, symmetric with init.** Gated on the SAME
 *    `isProductionEnvironment()` predicate the loaders use, so prod inits
 *    both AND emits, while dev / localhost / bots / webdriver init
 *    nothing AND emit nothing. No release-path-parity footgun.
 *
 * Event names + parameter keys are the existing cross-platform contract
 * (byte-identical with iOS / Android) — this module only forwards; it never
 * renames or reshapes.
 */
import type { AmplitudeEnrichmentPlugin } from './consentModePlugin'
import { isProductionEnvironment } from './environment'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import type { RouteDetailsAnalyticsReporter } from '@metro/route-details'
import {
  encodeRoutingTelemetryEvent,
  type RoutingTelemetryReporter,
} from '../state/routingTelemetryReporter'

// The analytics globals are installed at runtime by the consent loaders
// (all of them are absent in dev). Declared here as optional so
// the call sites stay type-safe without a separate ambient .d.ts.
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    amplitude?: {
      init?: (apiKey: string, userId?: string, options?: Record<string, unknown>) => void
      add?: (plugin: AmplitudeEnrichmentPlugin) => void
      track?: (event: string, params?: Record<string, unknown>) => void
      setTransport?: (transport: 'fetch' | 'xhr' | 'beacon') => void
      flush?: () => void
      /** The SDK's device id; absent on the pre-load command queue. */
      getDeviceId?: () => string | undefined
    }
  }
}

/**
 * Core sink. Forwards `(event, params)` to GA4 and Amplitude. No-op (and never throws) outside production or before the
 * relevant global has loaded. Each backend forward is isolated so a
 * failure in one never blocks the others.
 */
export function reportAnalyticsEvent(event: string, params: Record<string, unknown>): void {
  if (!isProductionEnvironment()) return

  // Omit null / undefined parameters before forwarding — cross-platform
  // alignment (Android omits absent ids; iOS omits nil). A null id must
  // ship as an ABSENT key, never as a literal `null` / `""`, so the
  // dashboards see the same shape on every platform.
  const cleanParams = omitNullish(params)

  // Google Analytics 4.
  try {
    window.gtag?.('event', event, cleanParams)
  } catch {
    // Vendor SDK threw — swallow (rule #2). Absence of the global is
    // handled by optional-chaining above, not by this catch.
  }

  // Amplitude.
  try {
    window.amplitude?.track?.(event, cleanParams)
  } catch {
    // swallow — see above.
  }
}

/**
 * Reserved event name for the web app-health / `reportError` channel
 * (`metro-health-events.md` §6). Web has NO native error API (`reportError`
 * is AppMetrica **mobile** only), so web health rolls up in Amplitude as a
 * normal event rather than the AppMetrica Errors report. Keep this exact string: the dashboard
 * segments on it.
 */
export const APP_HEALTH_ERROR_EVENT = 'app_health_error'

/**
 * Web app-health error sink (`metro-health-events.md` §6). Reports a handled,
 * non-fatal error / degraded state / silent fallback — the signal a crash
 * report can never show. There is no `reportError` channel on web, so this
 * maps the canonical cross-platform schema onto a reserved **product event**
 * `app_health_error` carrying the stable `id` + the `metadata` context as
 * params, forwarded through the same fail-safe / production-gated core sink
 * (→ Amplitude + GA4). Web health is queried in **Amplitude**
 * (a separate segment), mobile health in the AppMetrica Errors report — until
 * a web crash reporter (Sentry-class) lands.
 *
 * `id` is the stable, low-cardinality grouping code (§3 vocabulary) — NEVER
 * interpolate dynamic values into it (that shatters grouping); dynamic detail
 * goes in `message` / `metadata`. The `message` rides as a `message` param so
 * the human-readable detail is searchable alongside the grouped id. Like every
 * sink call this NEVER throws into the host (safe-client rule #2) — a health
 * emit must not become the failure it reports, especially on the cold-start /
 * first-frame path.
 */
export function reportAppHealthError(
  id: string,
  message: string,
  metadata: Record<string, unknown> = {},
): void {
  // `id` + `message` are first-class params alongside the metadata context.
  // omitNullish (inside reportAnalyticsEvent) drops absent optional metadata
  // keys (endpoint / status_code / latency_ms / fallback / placement_id / …)
  // so the wire shape matches the cross-platform OMIT-when-null convention.
  reportAnalyticsEvent(APP_HEALTH_ERROR_EVENT, { id, message, ...metadata })
}

/**
 * Returns a shallow copy of `params` with every `null` / `undefined`
 * value dropped, so the forwarded event carries only the keys that have
 * a real value. Keeps falsy-but-present values (`0`, `false`, `''`).
 */
function omitNullish(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const key in params) {
    const value = params[key]
    if (value !== null && value !== undefined) out[key] = value
  }
  return out
}

/**
 * Real {@link MetroAnalyticsReporter}. The project reporter signature is
 * already `(event, metadata) => void`, so this forwards verbatim into the
 * core sink. Replaces `noopMetroAnalyticsReporter` at every host call site
 * (the no-op stays for tests / harness pages that must not emit).
 */
export const realMetroAnalyticsReporter: MetroAnalyticsReporter = (event, metadata) => {
  reportAnalyticsEvent(event, metadata)
}

/**
 * Real {@link RouteDetailsAnalyticsReporter}. Same `(event, params)`
 * shape — forwards verbatim into the core sink.
 */
export const realRouteDetailsAnalytics: RouteDetailsAnalyticsReporter = (event, params) => {
  reportAnalyticsEvent(event, params)
}

/**
 * Real {@link RoutingTelemetryReporter}. Maps the typed union event to
 * its wire `(name, parameters)` shape via the shared encoder (kept
 * byte-identical with iOS / Android) before forwarding into the core sink.
 */
export const realRoutingTelemetryReporter: RoutingTelemetryReporter = (event) => {
  const { name, parameters } = encodeRoutingTelemetryEvent(event)
  reportAnalyticsEvent(name, parameters)
}
